import { useState, useCallback, useEffect, useRef } from 'react';
import { Tag, Settings2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { writeAuditLog } from '@/lib/audit';
import {
  getPrinterIp, setPrinterIp, sendZpl,
  skuLabelZpl, snLabelZpl,
  normaliseSerialNumbers, type SerialNumbersMap,
} from '@/lib/zebraPrinter';
import {
  printHtmlLabels,
  skuLabelHtml62mm, snLabelHtml62mm,
} from '@/lib/qzPrinter';

interface PrintZebraLabelsProps {
  orderId: string;
  orderNumber: string;
  sku: string;
  description?: string;
  quantity: number;
  serialNumber?: string | null;         // legacy scalar
  serialNumbers?: SerialNumbersMap | null; // jsonb per-unit
  onSerialsUpdated?: () => void;        // refetch after save
}

interface LabelRow {
  id: string;
  label: string;
  type: 'sku' | 'sn';
  unit?: number;
  selected: boolean;
  copies: number;
}

export function PrintZebraLabels({
  orderId, orderNumber, sku, description,
  quantity, serialNumber, serialNumbers,
  onSerialsUpdated,
}: PrintZebraLabelsProps) {
  const unitCount = Math.max(quantity || 1, 1);

  // Printer assignment from DB
  const [printerConfig, setPrinterConfig] = useState<{ label_printer_type: string; label_printer_name: string | null; label_printer_ip: string | null } | null>(null);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { console.warn('[PrintLabels] No auth user'); return; }
      const { data: tech, error: techErr } = await supabase
        .from('technicians')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (techErr || !tech) { console.warn('[PrintLabels] No technician record for user', user.id, techErr?.message); return; }
      const { data: assignment, error: assignErr } = await supabase
        .from('printer_assignments')
        .select('label_printer_type, label_printer_name, label_printer_ip')
        .eq('technician_id', tech.id)
        .single();
      if (assignErr) { console.warn('[PrintLabels] No printer assignment for tech', tech.id, assignErr.message); return; }
      if (assignment) {
        console.log('[PrintLabels] Printer config loaded:', assignment);
        setPrinterConfig(assignment);
      }
    })();
  }, []);

  // Dialog state
  const [open, setOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [ip, setIp] = useState(getPrinterIp);

  // Serial numbers (editable)
  const [serials, setSerials] = useState<SerialNumbersMap>({});
  const [savingSerials, setSavingSerials] = useState(false);
  const savedSerialsRef = useRef<SerialNumbersMap | null>(null);

  // Clear local override once props catch up from query refetch
  useEffect(() => {
    if (savedSerialsRef.current && serialNumbers) {
      const propsMatch = Object.keys(savedSerialsRef.current).every(
        k => serialNumbers[k] === savedSerialsRef.current![k]
      );
      if (propsMatch) savedSerialsRef.current = null;
    }
  }, [serialNumbers]);

  // Label rows
  const [rows, setRows] = useState<LabelRow[]>([]);

  // Print progress
  const [printing, setPrinting] = useState(false);
  const [progress, setProgress] = useState({ sent: 0, total: 0, failed: 0 });

  const openDialog = useCallback(() => {
    // Prefer locally-saved serials over props (props may be stale until query refetches)
    const sns = savedSerialsRef.current ?? normaliseSerialNumbers(serialNumbers, serialNumber, unitCount);
    setSerials(sns);

    // Build label rows: 1 SKU + 1 S/N per unit
    const labelRows: LabelRow[] = [
      { id: 'sku', label: `SKU — ${sku}`, type: 'sku', selected: true, copies: 3 },
    ];
    for (let i = 1; i <= unitCount; i++) {
      labelRows.push({
        id: `sn-${i}`,
        label: unitCount > 1 ? `S/N — Unit ${i}` : 'S/N',
        type: 'sn',
        unit: i,
        selected: true,
        copies: 3,
      });
    }
    setRows(labelRows);
    setProgress({ sent: 0, total: 0, failed: 0 });
    setPrinting(false);
    setOpen(true);
  }, [serialNumbers, serialNumber, unitCount, sku]);

  // ── Serial number editing ────────────────────────────────────────────────

  const updateSerial = (unit: string, value: string) => {
    setSerials(prev => ({ ...prev, [unit]: value }));
  };

  const saveSerials = async () => {
    setSavingSerials(true);
    const { error } = await supabase
      .from('assembly_orders')
      .update({
        serial_numbers: serials,
        serial_number: serials['1'] || null, // keep legacy field in sync
      })
      .eq('id', orderId);
    setSavingSerials(false);

    if (error) {
      toast.error('Failed to save serial numbers');
      return;
    }

    writeAuditLog({
      action: 'assembly_order_serial_numbers_updated',
      entity_type: 'assembly_order',
      entity_id: orderId,
      entity_label: orderNumber,
      new_value: serials,
    });

    savedSerialsRef.current = { ...serials };
    toast.success('Serial numbers saved');
    onSerialsUpdated?.();
  };

  // ── Row controls ─────────────────────────────────────────────────────────

  const toggleRow = (id: string) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, selected: !r.selected } : r));
  };

  const setCopies = (id: string, copies: number) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, copies: Math.max(1, copies) } : r));
  };

  const selectAll = (selected: boolean) => {
    setRows(prev => prev.map(r => ({ ...r, selected })));
  };

  // ── Print ────────────────────────────────────────────────────────────────

  const doPrint = async () => {
    const printerType = printerConfig?.label_printer_type || 'zebra';
    const printerName = printerConfig?.label_printer_name;
    const printerIp = printerConfig?.label_printer_ip || getPrinterIp();
    const useBrother = printerType === 'brother_62mm';

    // Validate
    if (useBrother && !printerName) {
      toast.error('No Brother printer configured — ask your admin to assign one');
      return;
    }
    if (!useBrother && !printerIp) {
      toast.error('Set the Zebra printer IP first');
      setConfigOpen(true);
      return;
    }

    const selected = rows.filter(r => r.selected);
    if (selected.length === 0) {
      toast.error('No labels selected');
      return;
    }

    // Check S/N labels have values
    for (const r of selected) {
      if (r.type === 'sn') {
        const sn = serials[String(r.unit)] || '';
        if (!sn) {
          toast.error(`Unit ${r.unit} has no serial number`);
          return;
        }
      }
    }

    setPrinting(true);

    try {
      if (useBrother) {
        // ── Brother 62mm via QZ Tray ──
        const htmlLabels: string[] = [];
        for (const row of selected) {
          for (let c = 0; c < row.copies; c++) {
            if (row.type === 'sku') {
              htmlLabels.push(...skuLabelHtml62mm(sku, description || ''));
            } else {
              htmlLabels.push(snLabelHtml62mm(serials[String(row.unit)] || ''));
            }
          }
        }
        await printHtmlLabels(printerName!, htmlLabels);
        toast.success(`${htmlLabels.length} label${htmlLabels.length > 1 ? 's' : ''} printed`);
      } else {
        // ── Zebra: direct HTTP to IP (existing flow) ──
        const total = selected.length;
        setProgress({ sent: 0, total, failed: 0 });
        let sent = 0;
        let failed = 0;

        for (const row of selected) {
          const zpl = row.type === 'sku'
            ? skuLabelZpl(sku, description || '', row.copies)
            : snLabelZpl(serials[String(row.unit)] || '', row.copies);

          const result = await sendZpl(zpl, printerIp);
          if (result.ok) sent++; else failed++;
          setProgress({ sent: sent + failed, total, failed });
        }

        if (failed === 0) {
          toast.success(`All ${sent} label${sent > 1 ? ' jobs' : ' job'} sent to printer`);
        } else {
          toast.error(`${failed} of ${total} label jobs failed`);
        }
      }
    } catch (e: any) {
      const msg = e.message || 'Print failed';
      if (msg.includes('Unable to connect')) {
        toast.error('QZ Tray not running — install from qz.io and start it');
      } else {
        toast.error(msg);
      }
    } finally {
      setPrinting(false);
    }
  };

  // ── Printer config ───────────────────────────────────────────────────────

  const saveIp = () => {
    setPrinterIp(ip);
    setConfigOpen(false);
    toast.success('Printer IP saved');
  };

  // ── Computed ─────────────────────────────────────────────────────────────

  const allSelected = rows.length > 0 && rows.every(r => r.selected);
  const noneSelected = rows.every(r => !r.selected);
  const progressPct = progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0;

  return (
    <>
      <Button variant="outline" size="sm" onClick={openDialog} title="Print Zebra labels">
        <Tag className="h-3.5 w-3.5 mr-1" />
        Labels
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-4 w-4" /> Print Labels
            </DialogTitle>
            <DialogDescription>
              {sku} — {unitCount} unit{unitCount > 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4">
            {/* ── Serial Numbers ─────────────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Serial Numbers</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs"
                  disabled={savingSerials}
                  onClick={saveSerials}
                >
                  {savingSerials ? 'Saving…' : 'Save'}
                </Button>
              </div>
              {Array.from({ length: unitCount }, (_, i) => {
                const key = String(i + 1);
                return (
                  <div key={key} className="flex items-center gap-2">
                    {unitCount > 1 && (
                      <span className="text-xs text-muted-foreground w-12 shrink-0">Unit {key}</span>
                    )}
                    <Input
                      placeholder="Enter serial number"
                      value={serials[key] || ''}
                      onChange={e => updateSerial(key, e.target.value)}
                      className="h-8 text-sm font-mono"
                    />
                  </div>
                );
              })}
            </div>

            {/* ── Label Selection ────────────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Labels</p>
                <button
                  className="text-xs text-primary hover:underline"
                  onClick={() => selectAll(!allSelected)}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              <div className="border rounded-md divide-y">
                {rows.map(row => {
                  const snValue = row.type === 'sn' ? (serials[String(row.unit)] || '') : '';
                  const missingValue = row.type === 'sn' && !snValue;
                  return (
                    <div
                      key={row.id}
                      className={`flex items-center gap-2 px-3 py-2 ${missingValue ? 'bg-amber-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={() => toggleRow(row.id)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{row.label}</p>
                        {row.type === 'sn' && (
                          <p className="text-xs font-mono text-muted-foreground truncate">
                            {snValue || <span className="text-amber-600 italic">not set</span>}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-xs text-muted-foreground">×</span>
                        <Input
                          type="number"
                          min={1}
                          max={99}
                          value={row.copies}
                          onChange={e => setCopies(row.id, parseInt(e.target.value) || 1)}
                          className="h-7 w-14 text-center text-sm px-1"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Progress Bar ───────────────────────────────────────────── */}
            {progress.total > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Sending to printer…</span>
                  <span>
                    {progress.sent} / {progress.total}
                    {progress.failed > 0 && (
                      <span className="text-red-600 ml-1">({progress.failed} failed)</span>
                    )}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      progress.failed > 0 ? 'bg-red-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-2 border-t">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 mr-auto"
              title="Printer settings"
              onClick={() => { setIp(getPrinterIp()); setConfigOpen(true); }}
            >
              <Settings2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button
              disabled={printing || noneSelected}
              onClick={doPrint}
            >
              <Printer className="h-4 w-4 mr-1" />
              {printing ? 'Printing…' : 'Print'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Printer IP config dialog ──────────────────────────────────── */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Zebra Printer</DialogTitle>
            <DialogDescription>Enter the printer's IP address on your network.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              placeholder="e.g. 192.168.1.100"
              value={ip}
              onChange={e => setIp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveIp()}
              className="font-mono"
            />
            <Button className="w-full" onClick={saveIp}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
